"use client";

import {JSX, useEffect, useState} from "react";
import type {Coordinate} from "ol/coordinate";
import CloudyDay from "./icons/CloudyDay.tsx";
import CloudyNight from "./icons/CloudyNight.tsx";
import ClearDay from "./icons/ClearDay.tsx";
import ClearNight from "./icons/ClearNight.tsx";
import Cloudy from "./icons/Cloudy.tsx";
import {formatTime, map, toLonLat} from "../utils.ts";
import {getWeatherForecast, WeatherForecast} from "../services/weather.ts";
import "./Weather.css";

function getIcon(code: number, isDay: boolean): JSX.Element {
    switch (code) {
        case 0:
            return isDay ? <ClearDay/> : <ClearNight/>;
        case 1:
        case 2:
            return isDay ? <CloudyDay/> : <CloudyNight/>;
        case 3:
            return <Cloudy/>;
    }

    return <></>;
}

export function Weather({coordinate}: { coordinate: Coordinate | null }) {
    const [time, setTime] = useState<Date | null>(null);

    useEffect(() => {
        setTime(new Date());
    }, []);

    useEffect(() => {
        const interval = setInterval(() => setTime(new Date()), 5000);
        return () => clearInterval(interval);
    }, []);

    const [weatherInThreeHours, setWeatherInThreeHours] = useState<WeatherForecast | null>(null);
    useEffect(() => {
        map(map(coordinate ?? null, toLonLat), getWeatherForecast)?.then(setWeatherInThreeHours);
    }, [coordinate, time]);

    if (weatherInThreeHours === null) {
        return <div className="weather-container">
            <div className="weather-time-container">
                <div className="weather-time">{map(time, formatTime)}</div>
                <div className="weather">
                    <div className="weather-items">
                        {
                            ...[null, null, null, null].map((_, index) =>
                                <div key={index} className="weather-item">
                                    <div className="weather-icon"></div>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>

            <div className="weather-temperature">
                <div className="loader"></div>
            </div>
        </div>;
    }

    const offsets = [5, 15, 25, 35];

    return (
        <div
            className="weather-widget"
            style={time === null ? {} : { '--minutes': time.getMinutes() + (time.getSeconds() / 60) }} // Передаем состояние в CSS
        >
            <svg width="103" height="103" viewBox="0 0 103 103" fill="none" xmlns="http://www.w3.org/2000/svg" className="weather-background">
                <defs>
                    <mask id="pill-mask">
                        <path
                            d="M102.726 86.393C102.726 96.1171 94.7882 104.152 85.2031 102.513C77.5794 101.21 70.1067 99.0573 62.9272 96.0835C50.3094 90.857 38.8445 83.1964 29.1872 73.5391C19.5299 63.8818 11.8693 52.4169 6.64284 39.7991C3.66902 32.6196 1.51638 25.1469 0.2132 17.5232C-1.42524 7.93809 6.60926 0 16.3334 0C26.0575 0 33.7196 8.00823 36.1816 17.4155C36.9739 20.4429 37.9736 23.4194 39.1764 26.3232C42.6332 34.6687 47.7 42.2516 54.0873 48.639C60.4747 55.0264 68.0576 60.0931 76.4031 63.5499C79.307 64.7527 82.2834 65.7524 85.3108 66.5447C94.7181 69.0067 102.726 76.6689 102.726 86.393Z"
                            stroke="#E0E0E0"
                            strokeWidth="70"
                            strokeLinecap="round"
                            fill="none"
                        />
                    </mask>
                </defs>
                <path d="M102.726 86.393C102.726 96.1171 94.7882 104.152 85.2031 102.513C77.5794 101.21 70.1067 99.0573 62.9272 96.0835C50.3094 90.857 38.8445 83.1964 29.1872 73.5391C19.5299 63.8818 11.8693 52.4169 6.64284 39.7991C3.66902 32.6196 1.51638 25.1469 0.2132 17.5232C-1.42524 7.93809 6.60926 0 16.3334 0C26.0575 0 33.7196 8.00823 36.1816 17.4155C36.9739 20.4429 37.9736 23.4194 39.1764 26.3232C42.6332 34.6687 47.7 42.2516 54.0873 48.639C60.4747 55.0264 68.0576 60.0931 76.4031 63.5499C79.307 64.7527 82.2834 65.7524 85.3108 66.5447C94.7181 69.0067 102.726 76.6689 102.726 86.393Z" fill="#E0E0E0"/>
            </svg>

            <div className="icons-track">
                {...weatherInThreeHours.map((weather, index) => (
                    <div key={index} className="weather-item icon fade-in"  style={{ '--icon-offset': offsets[index] + '%' }}>
                        {getIcon(weather.code, weather.isDay)}
                    </div>
                ))}
            </div>
        </div>
    );

    return <div style={{position: "relative", height: "6.25rem", width: "6.25rem"}}>
        <svg width="103" height="103" viewBox="0 0 103 103" fill="none" xmlns="http://www.w3.org/2000/svg" className="weather-background">
            <path d="M102.726 86.393C102.726 96.1171 94.7882 104.152 85.2031 102.513C77.5794 101.21 70.1067 99.0573 62.9272 96.0835C50.3094 90.857 38.8445 83.1964 29.1872 73.5391C19.5299 63.8818 11.8693 52.4169 6.64284 39.7991C3.66902 32.6196 1.51638 25.1469 0.2132 17.5232C-1.42524 7.93809 6.60926 0 16.3334 0C26.0575 0 33.7196 8.00823 36.1816 17.4155C36.9739 20.4429 37.9736 23.4194 39.1764 26.3232C42.6332 34.6687 47.7 42.2516 54.0873 48.639C60.4747 55.0264 68.0576 60.0931 76.4031 63.5499C79.307 64.7527 82.2834 65.7524 85.3108 66.5447C94.7181 69.0067 102.726 76.6689 102.726 86.393Z" fill="#E0E0E0"/>
        </svg>

        <div className="weather-items-x"
             style={time === null ? {} : {"--minutes": time.getMinutes() + (time.getSeconds() / 60)}}>
            {...weatherInThreeHours.map((weather, index) => (
                <div key={index} className="weather-item fade-in">
                    {getIcon(weather.code, weather.isDay)}
                </div>
            ))}
        </div>
    </div>;

    // return <div className="weather-container">
    //     <div className="weather-time-container">
    //         <div className="weather-time">{map(time, formatTime)}</div>
    //         <div className="weather">
    //             <div className="weather-items" style={time === null ? {} : {"--minutes": time.getMinutes() + (time.getSeconds() / 60)}}>
    //                 {...weatherInThreeHours.map((weather, index) => (
    //                     <div key={index} className="weather-item fade-in">
    //                         {getIcon(weather.code, weather.isDay)}
    //                     </div>
    //                 ))}
    //             </div>
    //         </div>
    //     </div>
    //
    //     <div className="weather-temperature">
    //         {weatherInThreeHours[0].temperature} &deg;C
    //     </div>
    // </div>;
}